// WhatsApp Ads (PRD §4.15). Click-to-WhatsApp campaigns with a tracked link and
// click counts. Put the link in Meta/Google ads; clicks open WhatsApp prefilled.
import { useState } from 'react';
import { useI18n } from '../lib/i18n';
import Modal from '../components/ui/Modal';
import { useTenant } from '../lib/tenant';
import { useAdCampaigns, saveCampaign, deleteCampaign, campaignLink, type AdCampaign, type AdInput } from '../lib/ads';

const inputCls = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';

export default function WhatsAppAds() {
  const { t } = useI18n();
  const { tenantId } = useTenant();
  const { campaigns, loading } = useAdCampaigns();
  const [draft, setDraft] = useState<AdInput | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<AdCampaign | null>(null);

  async function save() {
    if (!draft || !draft.name.trim() || !draft.prefill.trim()) return;
    setBusy(true); setErr(null);
    try { await saveCampaign(draft); setDraft(null); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  async function copy(c: AdCampaign) {
    if (!tenantId) return;
    try { await navigator.clipboard.writeText(campaignLink(tenantId, c.id)); setCopied(c.id); window.setTimeout(() => setCopied(null), 1500); } catch { /* noop */ }
  }
  async function doDelete() {
    if (!confirmDel) return;
    setBusy(true);
    try { await deleteCampaign(confirmDel.id); setConfirmDel(null); } finally { setBusy(false); }
  }

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h1 className="text-[22px] font-extrabold text-ink">{t('ads.title')}</h1>
        <button type="button" onClick={() => { setErr(null); setDraft({ name: '', prefill: 'Hi! I saw your ad and I\'m interested.' }); }} className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white sm:ms-auto">+ {t('ads.add')}</button>
      </div>
      <p className="mb-5 text-ink-3">{t('ads.subtitle')}</p>

      {loading ? (
        <div className="py-10 text-center text-sm text-ink-3">…</div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-card border border-dashed border-slate-300 bg-white p-10 text-center">
          <div className="text-3xl">📢</div>
          <p className="mt-2 text-sm text-ink-3">{t('ads.empty')}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {campaigns.map((c) => (
            <div key={c.id} className="flex flex-col rounded-card border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <b className="text-sm text-ink">{c.name}</b>
                <span className="whitespace-nowrap rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-bold text-primary">{c.clicks} {t('ads.clicks')}</span>
              </div>
              <p className="mt-1 line-clamp-2 flex-1 text-xs text-ink-3">{c.prefill}</p>
              {tenantId && <code className="mt-2 block overflow-x-auto rounded-[8px] bg-surface px-2 py-1.5 text-[10px] text-ink-2">{campaignLink(tenantId, c.id)}</code>}
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => void copy(c)} className="rounded-[8px] border border-slate-200 px-3 py-1.5 text-xs font-bold text-primary">{copied === c.id ? t('set.api.copied') : t('ads.copyLink')}</button>
                <button type="button" onClick={() => { setErr(null); setDraft({ id: c.id, name: c.name, prefill: c.prefill }); }} className="rounded-[8px] border border-slate-200 px-3 py-1.5 text-xs font-bold text-ink">{t('cat.edit')}</button>
                <button type="button" onClick={() => setConfirmDel(c)} className="rounded-[8px] border border-red-500 px-3 py-1.5 text-xs font-bold text-red-500">{t('cat.delete')}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? t('ads.editTitle') : t('ads.add')}
        footer={
          <>
            <button type="button" onClick={() => setDraft(null)} className="rounded-[9px] border border-slate-200 bg-white px-4 py-2 text-[13px] font-bold text-ink">{t('common.cancel')}</button>
            <button type="button" onClick={save} disabled={busy || !draft?.name.trim() || !draft?.prefill.trim()} className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">{busy ? '…' : t('common.apply')}</button>
          </>
        }
      >
        {draft && (
          <div className="space-y-3">
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder={t('ads.name')} className={inputCls} autoFocus />
            <div>
              <span className="mb-1 block text-xs font-bold text-ink-2">{t('ads.prefill')}</span>
              <textarea rows={3} value={draft.prefill} onChange={(e) => setDraft({ ...draft, prefill: e.target.value })} className={inputCls} />
              <p className="mt-1 text-[11px] text-ink-3">{t('ads.prefillNote')}</p>
            </div>
            {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
          </div>
        )}
      </Modal>

      <Modal
        open={confirmDel !== null}
        onClose={() => setConfirmDel(null)}
        title={t('cat.delete')}
        footer={
          <>
            <button type="button" onClick={() => setConfirmDel(null)} className="rounded-[9px] border border-slate-200 bg-white px-4 py-2 text-[13px] font-bold text-ink">{t('common.cancel')}</button>
            <button type="button" onClick={doDelete} disabled={busy} className="rounded-[9px] bg-red-500 px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">{t('cat.delete')}</button>
          </>
        }
      >
        <p className="text-sm text-ink-2">{confirmDel?.name}</p>
      </Modal>
    </div>
  );
}
